import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  MailOutlined,
  MoreOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined
} from "@ant-design/icons"
import {
  Alert,
  App,
  Button,
  Dropdown,
  Empty,
  Flex,
  Pagination,
  Select,
  Skeleton,
  Typography
} from "antd"
import type { ColumnsType } from "antd/es/table"
import { useEffect, useMemo, useState } from "react"

import { TextInput } from "../../components/Input/TextInput.tsx"
import Table from "../../components/Table"
import { ResizableHeaderCell } from "../../components/Table/ResizableHeaderCell.tsx"
import { useAppSelector } from "../../hooks/use-app-selector.ts"
import { useColumnResize } from "../../hooks/use-column-resize.tsx"
import { useDateFormatter } from "../../hooks/use-date-formatter.tsx"
import type { ColumnConfigItem } from "../User/redux/ui-preferences.types.ts"
import { showResponseMessage } from "../../utils/handlers/show-response-message.ts"
import { EmailTemplateFormModal } from "./components/EmailTemplateFormModal.tsx"
import { suggestDuplicateName } from "./helpers/suggest-duplicate-name.ts"
import {
  createEmailTemplate,
  deleteEmailTemplate,
  getEmailTemplates,
  updateEmailTemplate
} from "./api/services.ts"
import type {
  EmailTemplate,
  EmailTemplateMutateData
} from "./types/email-template.types.ts"

// Column widths are session-local (not saved to the account's UI
// preferences, unlike `EntityTable`'s tables) — this page has no existing
// preference key, and a resize/reload isn't a scenario worth a new
// persisted surface for a single settings list. `ColumnConfigItem.width`
// only ever gets read back out through `useColumnResize`'s debounced
// persist call, never into the initial render, so it's omitted here —
// the actual starting widths live on the `columns` array below.
const INITIAL_COLUMN_CONFIG: ColumnConfigItem[] = [
  { key: "name", visible: true },
  { key: "subject", visible: true },
  { key: "updated", visible: true }
]

const { Title, Text } = Typography

const PAGE_SIZE = 10

type ModalState =
  | { mode: "create" }
  | { mode: "edit"; template: EmailTemplate }
  | { mode: "duplicate"; template: EmailTemplate }
  | null

type SortKey = "updated" | "name"

export function EmailTemplatesPage() {
  const { modal, message } = App.useApp()
  const { formatDate, dateFormat } = useDateFormatter()
  const permissions = useAppSelector(
    (state) => state.auth.permissions?.codes?.settings?.email_templates
  )

  const [templates, setTemplates] = useState<EmailTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [loadFailed, setLoadFailed] = useState(false)
  const [search, setSearch] = useState("")
  const [sort, setSort] = useState<SortKey>("updated")
  const [page, setPage] = useState(1)
  const [modalState, setModalState] = useState<ModalState>(null)
  const [columnConfig, setColumnConfig] = useState<ColumnConfigItem[]>(
    INITIAL_COLUMN_CONFIG
  )
  const { decorateColumns, tableWrapRef } = useColumnResize<EmailTemplate>({
    fullConfig: columnConfig,
    setColumns: setColumnConfig
  })

  const loadTemplates = async () => {
    setLoading(true)
    setLoadFailed(false)
    const response = await getEmailTemplates({ page_size: 1000 })
    if (!response.ok) {
      setLoadFailed(true)
      setLoading(false)
      return
    }
    setTemplates(response.data?.results ?? [])
    setLoading(false)
  }

  useEffect(() => {
    void loadTemplates()
  }, [])

  const canCreate = !!permissions?.settings_email_template_create
  const canEdit = !!permissions?.settings_email_template_edit
  const canDelete = !!permissions?.settings_email_template_delete
  const canManageRow = canCreate || canEdit || canDelete

  const rows = useMemo(() => {
    const matching = templates.filter((t) =>
      t.name.toLowerCase().includes(search.trim().toLowerCase())
    )
    return [...matching].sort((a, b) =>
      sort === "name"
        ? a.name.localeCompare(b.name)
        : new Date(b.updated).getTime() - new Date(a.updated).getTime()
    )
  }, [templates, search, sort])

  // Deleting the last row on a page (or a search narrowing the list) must
  // never strand the user on a page past the end — clamp back whenever the
  // filtered list shrinks under the current page.
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(rows.length / PAGE_SIZE))
    setPage((p) => Math.min(p, maxPage))
  }, [rows.length])

  const paged = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
  const isSearching = search.trim().length > 0

  const handleDelete = (template: EmailTemplate) => {
    const confirmed = modal.confirm({
      // No `title` here on purpose: antd's Modal.confirm renders whatever
      // `title` is given into both the modal header AND the confirm body's
      // own title span, which doubles "Delete Email Template?" in the DOM.
      // Rendering both lines through `content` keeps the same copy on
      // screen with a single accessible occurrence of each string.
      content: (
        <>
          <Text strong className={"block text-base"}>
            Delete Email Template?
          </Text>
          <Text>
            {`Are you sure you want to delete "${template.name}"? This action cannot be undone.`}
          </Text>
        </>
      ),
      okText: "Delete Template",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        const response = await deleteEmailTemplate(template.id)
        if (!response.ok) {
          message.error(
            response.data?.details?.message ?? "Failed to delete the email template."
          )
          // Modal.confirm only keeps the dialog open on a *rejected* onOk —
          // a plain return reads as success and closes it, so a failed
          // delete would flash the error toast and vanish with no retry.
          throw response
        }
        showResponseMessage(response, "Email template deleted.")
        await loadTemplates()
      }
    })
    // `useModal`'s hook instance only swallows a rejected onOk (rather than
    // re-throwing it as a genuine unhandled rejection, meant for errors
    // nobody chose to handle) once something reads its own promise —
    // `throw response` above is a deliberate, already-handled control-flow
    // signal, not an unexpected exception, so this opts into that.
    confirmed.then(
      () => {},
      () => {}
    )
  }

  // Returns whether the save actually landed — the modal keeps itself (and
  // the user's typed values) open on `false` rather than closing unconditionally.
  const handleSubmit = async (values: EmailTemplateMutateData): Promise<boolean> => {
    if (!modalState) return false

    if (modalState.mode === "edit") {
      const response = await updateEmailTemplate(modalState.template.id, values)
      if (!response.ok) {
        message.error(
          response.data?.details?.message ?? "Failed to update the email template."
        )
        return false
      }
      showResponseMessage(response, "Email template updated.")
    } else {
      // create AND duplicate both post to the same create call — there is
      // no dedicated backend duplicate endpoint.
      const response = await createEmailTemplate(values)
      if (!response.ok) {
        message.error(
          response.data?.details?.message ?? "Failed to create the email template."
        )
        return false
      }
      showResponseMessage(response, "Email template created.")
    }

    await loadTemplates()
    return true
  }

  const initialValuesFor = (
    state: ModalState
  ): Partial<EmailTemplateMutateData> | undefined => {
    if (!state) return undefined
    if (state.mode === "create") return undefined
    if (state.mode === "edit") return state.template
    return {
      name: suggestDuplicateName(state.template.name, templates),
      subject: state.template.subject,
      body: state.template.body
    }
  }

  const existingNamesExcluding = (state: ModalState) =>
    templates
      .filter((t) => !(state?.mode === "edit" && t.id === state.template.id))
      .map((t) => t.name)

  const columns: ColumnsType<EmailTemplate> = [
    {
      key: "name",
      title: "Template Name",
      width: 280,
      render: (_, record) => (
        <Flex align={"center"} gap={10} className={"min-w-0"}>
          <div
            aria-hidden
            className={
              "flex size-8 shrink-0 items-center justify-center rounded-lg bg-sky-50 text-sky-500"
            }
          >
            <MailOutlined />
          </div>
          <Text strong ellipsis>
            {record.name}
          </Text>
        </Flex>
      )
    },
    {
      // No initial width: the one column left to absorb whatever space
      // Name/Last Updated/Actions don't claim, per CSS's own fixed-table-
      // layout rule for a column with no explicit width.
      key: "subject",
      title: "Subject",
      ellipsis: true,
      render: (_, record) => record.subject
    },
    {
      key: "updated",
      title: "Last Updated",
      width: 160,
      render: (_, record) => (
        <Text type={"secondary"}>
          {formatDate(record.updated, dateFormat) as string}
        </Text>
      )
    },
    {
      key: "actions",
      title: "Actions",
      width: 90,
      align: "center",
      fixed: "right",
      render: (_, record) =>
        canManageRow ? (
          <Dropdown
            trigger={["click"]}
            placement={"bottomRight"}
            menu={{
              items: [
                ...(canEdit
                  ? [{ key: "edit", icon: <EditOutlined />, label: "Edit" }]
                  : []),
                // Duplicating creates a new template, so — mirroring
                // DocumentTemplatesListItem's copyAllowed — it rides
                // the create permission, not edit.
                ...(canCreate
                  ? [
                      {
                        key: "duplicate",
                        icon: <CopyOutlined />,
                        label: "Duplicate"
                      }
                    ]
                  : []),
                ...(canDelete
                  ? [
                      {
                        key: "delete",
                        icon: <DeleteOutlined />,
                        label: "Delete",
                        danger: true
                      }
                    ]
                  : [])
              ],
              onClick: ({ key }) => {
                if (key === "edit")
                  setModalState({ mode: "edit", template: record })
                if (key === "duplicate")
                  setModalState({ mode: "duplicate", template: record })
                if (key === "delete") handleDelete(record)
              }
            }}
          >
            <Button
              size={"small"}
              icon={<MoreOutlined />}
              aria-label={`Actions for ${record.name}`}
            />
          </Dropdown>
        ) : null
    }
  ]

  // Only the first load shows the full-page skeleton — a post-mutation
  // refresh (e.g. after a save) reuses `loading` too, but replacing the
  // whole page while `EmailTemplateFormModal` is still closing itself would
  // unmount the modal out from under its own `onClose()`.
  if (loading && templates.length === 0) {
    return (
      <Flex vertical gap={"small"} className={"p-4"}>
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton.Node active className={"w-full h-[68px]"} key={i} />
        ))}
      </Flex>
    )
  }

  if (loadFailed) {
    return (
      <Alert
        type={"error"}
        showIcon
        description={"Something went wrong loading email templates."}
        action={
          <Button
            icon={<ReloadOutlined />}
            onClick={() => void loadTemplates()}
            size={"small"}
          >
            Retry
          </Button>
        }
      />
    )
  }

  return (
    <Flex vertical gap={"middle"} className={"size-full"}>
      <Flex align={"center"} gap={"small"} wrap justify={"space-between"}>
        <Flex vertical gap={0}>
          <Title level={3} className={"m-0 text-start"}>
            Email Templates
          </Title>
          <Text type={"secondary"}>
            Create and manage reusable email templates for customer
            communication.
          </Text>
        </Flex>
        {canCreate && (
          <Button
            type={"primary"}
            icon={<PlusOutlined />}
            onClick={() => setModalState({ mode: "create" })}
          >
            New Email Template
          </Button>
        )}
      </Flex>

      <Flex gap={"small"}>
        <TextInput
          prefix={<SearchOutlined className={"text-text-tertiary"} />}
          placeholder={"Search email templates..."}
          allowClear
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setPage(1)
          }}
          className={"flex-1"}
        />
        <Select<SortKey>
          value={sort}
          onChange={setSort}
          style={{ width: 180 }}
          options={[
            { value: "updated", label: "Last updated" },
            { value: "name", label: "Name (A–Z)" }
          ]}
        />
      </Flex>

      {rows.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            isSearching
              ? "No email templates match your search."
              : "No email templates are available."
          }
        >
          {!isSearching && canCreate && (
            <Button
              type={"primary"}
              icon={<PlusOutlined />}
              onClick={() => setModalState({ mode: "create" })}
            >
              Create Email Template
            </Button>
          )}
        </Empty>
      ) : (
        <div
          ref={tableWrapRef}
          className={"rounded-lg border border-border overflow-hidden"}
        >
          <Table<EmailTemplate>
            rowKey={"id"}
            tableLayout={"fixed"}
            columns={decorateColumns(columns)}
            dataSource={paged}
            pagination={false}
            withOutPagination
            components={{ header: { cell: ResizableHeaderCell } }}
          />
        </div>
      )}

      {rows.length > 0 && (
        <Flex align={"center"} justify={"space-between"}>
          <Text type={"secondary"}>{rows.length} templates</Text>
          {rows.length > PAGE_SIZE && (
            <Pagination
              size={"small"}
              current={page}
              pageSize={PAGE_SIZE}
              total={rows.length}
              onChange={setPage}
              showSizeChanger={false}
            />
          )}
        </Flex>
      )}

      {modalState && (
        <EmailTemplateFormModal
          open={!!modalState}
          mode={
            modalState.mode === "edit"
              ? "edit"
              : modalState.mode === "duplicate"
                ? "duplicate"
                : "create"
          }
          initialValues={initialValuesFor(modalState)}
          existingNames={existingNamesExcluding(modalState)}
          onClose={() => setModalState(null)}
          onSubmit={handleSubmit}
        />
      )}
    </Flex>
  )
}

export default EmailTemplatesPage
